import React, {useEffect, useMemo, useState} from 'react';
import useQuestionStore from '../store/navigation';
import {
  MultiOption,
  MultiSelectorQuestion,
  QUESTIONIDS,
} from '../types/question';
import QuestionMultiSelectorContainer from './SelectorContainer';
import {useResultStore} from '../store/responses';
import {sendAnalyticPayload} from '../utils/analytic';
import {QuestionData, QuestionStepName} from '../constants/questions';

interface Props extends Partial<MultiSelectorQuestion> {
  title: string;
  subHeading?: string;
  multiSelectOptions: MultiOption[];
  nextStepId?: (typeof QUESTIONIDS)[keyof typeof QUESTIONIDS];
  maxOptions?: number;
  handleNextStepBySelectedItems?: (selectedItems: MultiOption[]) => void;
  handleMultipleOptionChange?: (value: string) => void;
  isNoDisabled?: boolean;
}

const QuestionMultiSelector: React.FC<Props> = ({
  subHeading,
  title,
  multiSelectOptions,
  nextStepId,
  maxOptions,
  handleNextStepBySelectedItems,
  handleMultipleOptionChange,
  isNoDisabled,
}) => {
  const {setCurrentQuestion, currentQuestion} = useQuestionStore();
  const {result, updateQuestion} = useResultStore();
  const maxOption = maxOptions || 3;

  const resultData = useMemo(() => {
    return (result[currentQuestion] as Array<string>) || [];
  }, [currentQuestion, result]);
  const [selectedOptions, setSelectedOptions] = useState<MultiOption[]>(
    multiSelectOptions.map((option) => ({
      ...option,
      isSelect: option.isSelect ?? resultData.includes(option.value),
    })),
  );

  useEffect(() => {
    if (multiSelectOptions && multiSelectOptions?.length > 0) {
      if (isNoDisabled) {
        setSelectedOptions(
          multiSelectOptions.map((option) => ({
            isSelect: option.isSelect || resultData.includes(option.value),
            ...option,
          })),
        );
      } else {
        if (resultData && resultData.length > 0) {
          setSelectedOptions(
            multiSelectOptions.map((option) => ({
              ...option,
              isSelect: resultData.includes(option.value),
              isDisabled: !resultData.includes(option.value),
            })),
          );
          return;
        }
        setSelectedOptions(
          multiSelectOptions.map((option) => ({
            ...option,
            isSelect: option.isSelect ?? false,
          })),
        );
        return;
      }
    } else {
      setSelectedOptions([]);
    }
  }, [multiSelectOptions, resultData, isNoDisabled]);
  const checkIsDisableByExclude = (
    excludes: string[],
    selectedArrayAditional: MultiOption[],
  ) => {
    const getSlectedValue = [...selectedArrayAditional]
      .filter((option) => option.isSelect)
      .map((option) => option.value);
    const result = excludes.find((item) => getSlectedValue.includes(item));
    return !!result;
  };

  const handleOptionChange = (optionSelected: MultiOption) => {
    if (handleMultipleOptionChange) {
      handleMultipleOptionChange(optionSelected.value);
    } else {
      const currentSelectedOptions = selectedOptions.filter(
        (option) => option.value !== optionSelected.value,
      );
      const selectedCount = selectedOptions.filter(
        (option) => option.isSelect,
      ).length;

      if (optionSelected.isSelect && selectedCount === maxOption) {
        const updatedOptions = [...selectedOptions].map((option) => {
          const isExcludedBySelected = checkIsDisableByExclude(
            option?.excludes || [],
            [...currentSelectedOptions, {...optionSelected, isSelect: false}],
          );
          if (optionSelected.value === option.value) {
            return {...option, isDisabled: false, isSelect: false};
          }
          if (isExcludedBySelected) {
            return {
              ...option,
              isDisabled: true,
            };
          } else {
            return {...option, isDisabled: false};
          }
        });
        setSelectedOptions(updatedOptions);
        return;
      }
      if (!optionSelected.isSelect && selectedCount === maxOption - 1) {
        const updatedOptions = selectedOptions.map((option) => {
          const isExcludedBySelected = checkIsDisableByExclude(
            option?.excludes || [],
            [...currentSelectedOptions],
          );
          if (optionSelected.value === option.value) {
            return {...option, isDisabled: false, isSelect: true};
          }
          if (isExcludedBySelected) {
            return {
              ...option,
              isDisabled: true,
            };
          } else {
            return {...option, isDisabled: !option.isSelect};
          }
        });
        setSelectedOptions(updatedOptions);
        return;
      }

      // Update exclusion logic
      const updatedOptions = selectedOptions.map((option) => {
        const isExcludedBySelected =
          optionSelected.excludes &&
          optionSelected.excludes.includes(option.value);
        const isExcludedByOther =
          option.excludes && option.excludes.includes(optionSelected.value);

        if (isExcludedBySelected || isExcludedByOther) {
          return {
            ...option,
            isDisabled: option.isSelect || !optionSelected.isSelect,
          };
        } else if (option.value === optionSelected.value) {
          return {...option, isSelect: !option.isSelect, isDisabled: false};
        } else {
          return option;
        }
      });
      setSelectedOptions(updatedOptions);
    }
  };

  const handleSubmitNext = () => {
    const selectedItems = selectedOptions.filter((option) => option.isSelect);
    if (selectedItems.length >= 1) {
      const questionName = QuestionStepName[currentQuestion] || currentQuestion;
      updateQuestion({
        id: currentQuestion,
        value: selectedItems.map((item) => item.value),
      });
      sendAnalyticPayload({
        process: {
          StepName: questionName,
          StepNumber:
            QuestionData[currentQuestion]?.progressValue.toString() || '',
        },
        question: [
          {
            Question: questionName,
            Answer: [
              {
                value: selectedItems.map((item) => item.label).join(' | '),
                type: 'array',
              },
            ],
          },
        ],
      });
      handleNextStepBySelectedItems
        ? handleNextStepBySelectedItems?.(selectedItems)
        : setCurrentQuestion(nextStepId);
    }
  };

  const isDisabledButton = useMemo(() => {
    if (selectedOptions.filter((option) => option.isSelect).length >= 1) {
      return false;
    } else {
      return true;
    }
  }, [selectedOptions]);

  return (
    <QuestionMultiSelectorContainer
      title={title || ''}
      selectedOptions={selectedOptions}
      isDisabledButton={isDisabledButton}
      handleOptionChange={handleOptionChange}
      handleSubmitNext={handleSubmitNext}
      subHeading={subHeading ?? ''}
    />
  );
};

export default QuestionMultiSelector;
